/**
 * 🚨 GENERIC ERROR PAGE
 * Flexible error component for various HTTP status codes
 * Handles any error with appropriate messaging and actions
 */
import React from 'react';
import { vars } from '../../../../styles/vars.css';
import { Link, useNavigate } from 'react-router-dom';
import * as styles from './errorPages.css';

interface GenericErrorPageProps {
  /** HTTP status code */
  statusCode: number;
  /** Error title */
  title?: string;
  /** Error message */
  message?: string;
  /** Error icon emoji */
  icon?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Show technical details */
  showTechnicalDetails?: boolean;
  /** Custom suggestions */
  suggestions?: string[];
  /** Additional error context */
  errorContext?: {
    timestamp?: string;
    requestId?: string;
    method?: string;
    endpoint?: string;
  };
}

// Default configurations for common error codes
const errorConfigs = {
  400: {
    title: 'Requête Incorrecte',
    icon: '❌',
    message: 'La requête envoyée au serveur contient des erreurs ou des données manquantes.',
    suggestions: [
      'Vérifiez les données saisies dans le formulaire',
      'Assurez-vous que tous les champs requis sont remplis',
      'Contactez le support si le problème persiste'
    ]
  },
  401: {
    title: 'Authentification Requise',
    icon: '🔐',
    message: 'Vous devez vous connecter pour accéder à cette ressource.',
    suggestions: [
      'Connectez-vous avec vos identifiants',
      'Vérifiez que votre session n\'a pas expiré',
      'Créez un compte si vous n\'en avez pas'
    ]
  },
  408: {
    title: 'Délai d\'Attente Dépassé',
    icon: '⏱️',
    message: 'La requête a pris trop de temps à être traitée.',
    suggestions: [
      'Vérifiez votre connexion internet',
      'Réessayez dans quelques instants',
      'Contactez le support si le problème persiste'
    ]
  },
  429: {
    title: 'Trop de Requêtes',
    icon: '🚫',
    message: 'Vous avez fait trop de requêtes. Veuillez patienter avant de réessayer.',
    suggestions: [
      'Attendez quelques minutes avant de réessayer',
      'Réduisez la fréquence de vos actions',
      'Contactez le support pour augmenter vos limites'
    ]
  },
  502: {
    title: 'Passerelle Défaillante',
    icon: '🌐',
    message: 'Le serveur a reçu une réponse invalide d\'un serveur en amont.',
    suggestions: [
      'Réessayez dans quelques instants',
      'Le problème est temporaire et devrait se résoudre',
      'Contactez le support si cela persiste'
    ]
  },
  503: {
    title: 'Service Indisponible',
    icon: '🔧',
    message: 'Le service est temporairement indisponible, probablement pour maintenance.',
    suggestions: [
      'Réessayez dans quelques minutes',
      'Consultez la page de statut du service',
      'La maintenance devrait être terminée bientôt'
    ]
  }
};

const GenericErrorPage: React.FC<GenericErrorPageProps> = ({
  statusCode,
  title,
  message,
  icon,
  showRetry = true,
  showTechnicalDetails = false,
  suggestions,
  errorContext
}) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = React.useState(false);

  // Get default config for the status code
  const config = errorConfigs[statusCode as keyof typeof errorConfigs];
  
  // Use props or fallback to config or defaults
  const errorTitle = title || config?.title || `Erreur ${statusCode}`;
  const errorMessage = message || config?.message || 'Une erreur inattendue s\'est produite.';
  const errorIcon = icon || config?.icon || '⚠️';
  const errorSuggestions = suggestions || config?.suggestions || [
    'Réessayez votre action',
    'Vérifiez votre connexion',
    'Contactez le support si nécessaire'
  ];

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className={styles.errorContainer}>
      {/* Breadcrumb for context */}
      <nav className={styles.breadcrumb}>
        <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
        <span> / </span>
        <span>Erreur {statusCode}</span>
      </nav>

      <div className={styles.errorContent}>
        {/* Error Icon */}
        <div className={styles.errorIcon}>
          {errorIcon}
        </div>

        {/* Error Code */}
        <h1 className={styles.errorCode}>
          {statusCode}
          <span className={styles.srOnly}>Erreur {statusCode}</span>
        </h1>

        {/* Error Title */}
        <h2 className={styles.errorTitle}>
          {errorTitle}
        </h2>

        {/* Error description */}
        <p className={styles.errorDescription}>
          {errorMessage}
        </p>

        {/* Suggestions */}
        <div>
          <h3 style={{ 
            fontSize: '1.1rem', 
            marginBottom: '1rem', 
            color: 'var(--colors-text)',
            fontWeight: 600 
          }}>
            Solutions suggérées
          </h3>
          <ul className={styles.suggestionsList}>
            {errorSuggestions.map((suggestion, index) => (
              <li key={index} className={styles.suggestionItem}>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* action Buttons */}
        <div className={styles.actionButtons}>
          {showRetry && (
            <button 
              onClick={handleRetry}
              className={styles.primaryButton}
            >
              <span>🔄</span>
              Réessayer
            </button>
          )}
          <Link to="/" className={styles.secondaryButton}>
            <span>🏠</span>
            Retour à l'accueil
          </Link>
          <button 
            onClick={handleGoBack}
            className={styles.secondaryButton}
          >
            <span>⬅️</span>
            Page précédente
          </button>
        </div>

        {/* Status Indicator */}
        <div className={styles.statusIndicator}>
          <div className={styles.statusDot} style={{ 
            backgroundColor: statusCode >= 500 ? vars.color.danger : vars.color.warning 
          }}></div>
          <span>
            {statusCode >= 500 ? 'Problème serveur détecté' : 'Erreur client détectée'}
          </span>
        </div>

        {/* Technical Details (if enabled) */}
        {showTechnicalDetails && (
          <div className={styles.technicalDetails}>
            <h4 
              className={styles.technicalTitle}
              onClick={() => setShowDetails(!showDetails)}
            >
              <span>{showDetails ? '🔽' : '▶️'}</span>
              Détails techniques
            </h4>
            {showDetails && (
              <div className={styles.technicalContent}>
                {`HTTP Status: ${statusCode}
Timestamp: ${errorContext?.timestamp || new Date().toISOString()}
Request ID: ${errorContext?.requestId || Math.random().toString(36).substr(2, 9)}
Method: ${errorContext?.method || 'GET'}
Endpoint: ${errorContext?.endpoint || window.location.pathname}
User Agent: ${navigator.userAgent}
Referrer: ${document.referrer || 'Direct access'}`}
              </div>
            )}
          </div>
        )}

        {/* Help section */}
        <div className={styles.helpSection}>
          <h3 className={styles.helpTitle}>
            Besoin d'assistance ?
          </h3>
          <p className={styles.helpText}>
            Si cette erreur persiste ou si vous avez besoin d'aide, 
            n'hésitez pas à contacter notre équipe de support.
          </p>
          <div className={styles.contactInfo}>
            <a href="mailto:jf.c.webcraft@gmail.com" className={styles.contactLink}>
              📧 jf.c.webcraft@gmail.com
            </a>
            <span style={{ fontSize: '0.9rem', color: 'var(--colors-text-secondary)' }}>
              🆔 Code d'erreur: ERR_{statusCode}_{Date.now().toString().slice(-6)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericErrorPage;
